import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import VenueForm from '../../global/Venue/VenueForm';
import { addVenueToOrganization, fetchVenueById, updateVenue } from '../../../services/Http/Api';
import StyledModal from '../../../shared/layout/modals/StyledModal';
import ModalHeader from '../../../shared/layout/modals/ModalHeader';
import ModalTitle from '../../../shared/layout/modals/ModalTitle';
import ModalBody from '../../../shared/layout/modals/ModalBody';
import CloseButton from '../../../shared/layout/modals/CloseButton';
import { toast } from 'react-toastify';

const VenueModal = ({ show, mode, venueId, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [venue, setVenue] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!show) {
            setVenue(null);
            setLoading(false);
            return;
        }

        if (mode !== 'edit' || !venueId) {
            setVenue(null);
            return;
        }

        const loadVenue = async () => {
            setLoading(true);
            try {
                const data = await fetchVenueById(venueId);
                setVenue(data);
            } catch (error) {
                console.error('Error fetching venue:', error);
                toast.error(t('Venues.VenueForm.loadError'));
            } finally {
                setLoading(false);
            }
        };

        loadVenue();
    }, [show, mode, venueId, t]);

    if (!show) return null;

    const handleSave = async (venueData) => {
        try {
            if (mode === 'edit' && venueId) {
                await updateVenue(venueId, venueData);
                toast.success(t('Venues.VenueForm.updateSuccess'));
            } else {
                await addVenueToOrganization(venueData);
                toast.success(t('Venues.VenueForm.createSuccess'));
            }
            onSaved();
        } catch (error) {
            console.error('Error saving venue:', error);
            if (error.response?.status === 422) {
                toast.error(t('Venues.VenueForm.validationError'));
            } else {
                toast.error(t('Venues.VenueForm.saveError'));
            }
        }
    };

    const title =
        mode === 'edit'
            ? t('Venues.VenueForm.title_edit', { venueName: venue?.name || '' })
            : t('Venues.VenueForm.title_create');

    return (
        <ModalWrapper>
            <Backdrop onClick={onClose} />
            <StyledModal>
                <ModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>
                <ModalBody>
                    {loading ? (
                        <LoadingContainer>
                            <LoadingSpinner />
                            <LoadingText>{t('Venues.VenueForm.loading')}</LoadingText>
                        </LoadingContainer>
                    ) : (
                        <VenueForm venue={venue} onSave={handleSave} />
                    )}
                </ModalBody>
            </StyledModal>
        </ModalWrapper>
    );
};

export default VenueModal;

// Styled Components
const ModalWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1050;
`;

const Backdrop = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    z-index: 1040;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2.5rem;
    gap: 1rem;
`;

const LoadingSpinner = styled.div`
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #821692;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const LoadingText = styled.div`
    color: #6b7280;
    font-size: 0.95rem;
    text-align: center;
`;
